import {
  Map as MapaGL, NavigationControl, ScaleControl, AttributionControl, GeolocateControl, setWorkerUrl, addProtocol,
  type GeoJSONSource, type MapMouseEvent, type LngLatLike, type RasterTileSource,
} from 'maplibre-gl';
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { Protocol } from 'pmtiles';
import type { GeoJSON, Feature } from 'geojson';
import 'maplibre-gl/dist/maplibre-gl.css';

// MapLibre 6 deduce la URL de su worker de import.meta.url, que Vite no sirve ni en desarrollo
// ni en el bundle; se le da la URL que Vite genera para ese fichero como worker.
setWorkerUrl(maplibreWorkerUrl);

// Protocolo pmtiles:// para las teselas nacionales del Catastro (fase 5), leidas por rangos HTTP.
if (BASE_PMTILES) {
  const protocolo = new Protocol();
  addProtocol('pmtiles', protocolo.tile);
}
import { estiloInicial, CAPAS_BASE, IDS_BASE, URL_WMS_HISTORICO, BASE_PMTILES } from './capas';
import type { CapaBase } from '../app/estado';
import type { Bbox, Punto, MultiPoligono } from '../geo/geometria';
import { areaAnillo, longitud } from '../geo/geometria';
import { aUtm, husoParaLonLat, proyectar, proyectarBbox, proyectarAnillo } from '../geo/proyecciones';
import type { FeatureCollectionSimple, FeatureCollectionLineas } from '../db/protocolo';

/**
 * Envoltorio de MapLibre con lo que necesita la aplicacion: capas base, Catastro WMS, la capa
 * vectorial de la zona cargada con estados (hover, seleccion, resultado, candidato), dibujo de
 * rectangulo, medicion, terreno 3D y utilidades de coordenadas en UTM.
 */
export interface Vista {
  centro: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
}

export interface Medicion {
  puntos: Punto[];
  longitudM: number;
  areaM2: number | null;
}

type Cb<T> = (v: T) => void;

export class Mapa {
  readonly map: MapaGL;
  private hoverRc: string | null = null;
  private seleccionRc: string | null = null;
  private resultadosRcs = new Set<string>();
  private dibujando: { inicio: Punto; epsg: number } | null = null;
  private medicion: Punto[] = [];
  private epsgMedicion = 25830;
  listo: Promise<void>;

  onClickParcela: Cb<{ rc: string | null; lonLat: [number, number] }> | null = null;
  onZonaDibujada: Cb<{ bbox: Bbox; epsg: number }> | null = null;
  onMedicion: Cb<Medicion | null> | null = null;
  onPuntoElegido: Cb<[number, number]> | null = null;
  onCursor: Cb<[number, number] | null> | null = null;
  onMovimiento: Cb<Vista> | null = null;

  herramienta: 'ninguna' | 'zona' | 'medir' | 'punto' = 'ninguna';

  constructor(contenedor: HTMLElement, vista: Vista) {
    this.map = new MapaGL({
      container: contenedor,
      style: estiloInicial(),
      center: vista.centro,
      zoom: vista.zoom,
      bearing: vista.bearing,
      pitch: vista.pitch,
      maxPitch: 80,
      attributionControl: false,
      hash: false,
    });
    this.map.addControl(new NavigationControl({ visualizePitch: true }), 'top-right');
    this.map.addControl(new ScaleControl({ maxWidth: 140, unit: 'metric' }), 'bottom-right');
    this.map.addControl(new AttributionControl({ compact: false }), 'bottom-right');
    this.map.addControl(new GeolocateControl({ positionOptions: { enableHighAccuracy: true }, trackUserLocation: false }), 'top-right');

    // 'style.load' llega en cuanto el estilo esta parseado (las capas ya existen); 'load' espera
    // ademas a las primeras teselas de los servidores publicos, que a veces tardan minutos.
    this.listo = new Promise((r) => {
      if (this.map.isStyleLoaded()) r();
      else this.map.once('style.load', () => r());
    });
    this.conectarEventos();
  }

  // ---------- Vista y coordenadas ----------

  vista(): Vista {
    const c = this.map.getCenter();
    return { centro: [c.lng, c.lat], zoom: this.map.getZoom(), bearing: this.map.getBearing(), pitch: this.map.getPitch() };
  }

  epsgVista(): number {
    const c = this.map.getCenter();
    return husoParaLonLat(c.lng, c.lat);
  }

  /** Caja de la vista en el huso indicado (o el de la vista). */
  bboxVistaUtm(epsg?: number): { bbox: Bbox; epsg: number } {
    const e = epsg ?? this.epsgVista();
    const b = this.map.getBounds();
    const bbox = proyectarBbox(4326, e, [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
    return { bbox, epsg: e };
  }

  irA(lon: number, lat: number, zoom = 17): void {
    this.map.flyTo({ center: [lon, lat], zoom, duration: 900 });
  }

  encajarBbox(bbox: Bbox, epsg: number, maxZoom = 18): void {
    const g = proyectarBbox(epsg, 4326, bbox);
    this.map.fitBounds([[g[0], g[1]], [g[2], g[3]]], { padding: 60, maxZoom, duration: 700 });
  }

  // ---------- Capas ----------

  setBase(base: CapaBase): void {
    for (const id of IDS_BASE) this.map.setLayoutProperty(id, 'visibility', id === CAPAS_BASE[base].id ? 'visible' : 'none');
  }

  setRotulos(visible: boolean): void {
    this.map.setLayoutProperty('rotulos', 'visibility', visible ? 'visible' : 'none');
  }

  setCatastroWms(visible: boolean, opacidad: number): void {
    this.map.setLayoutProperty('catastro-wms', 'visibility', visible ? 'visible' : 'none');
    this.map.setPaintProperty('catastro-wms', 'raster-opacity', opacidad);
  }

  setZonaVisible(visible: boolean): void {
    for (const id of ['zona-fill', 'zona-line', 'zona-etiquetas']) this.map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
  }

  setOrtoHistorica(capa: string | null): void {
    if (capa) {
      const src = this.map.getSource('src-historico') as RasterTileSource | undefined;
      src?.setTiles([URL_WMS_HISTORICO.replace('{capa}', capa)]);
    }
    this.map.setLayoutProperty('historico', 'visibility', capa ? 'visible' : 'none');
  }

  setEdificios3d(visible: boolean): void {
    this.map.setLayoutProperty('partes-3d', 'visibility', visible ? 'visible' : 'none');
  }

  setAguaVisible(visible: boolean): void {
    for (const id of ['agua-line', 'agua-etiquetas']) this.map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
  }

  /** Terreno 3D (Terrain-RGB de IDEE) con exageracion; en 2D se desactiva y se endereza la camara. */
  set3d(activo: boolean, exageracion = 1): void {
    if (activo) {
      this.map.setTerrain({ source: 'src-terreno', exaggeration: exageracion });
      this.map.setSky({
        'sky-color': '#88b4e0', 'horizon-color': '#e6eef7', 'fog-color': '#ffffff',
        'sky-horizon-blend': 0.6, 'horizon-fog-blend': 0.7, 'fog-ground-blend': 0.4, 'atmosphere-blend': ['interpolate', ['linear'], ['zoom'], 0, 1, 10, 1, 12, 0],
      });
      if (this.map.getPitch() < 20) this.map.easeTo({ pitch: 55, duration: 800 });
    } else {
      this.map.setTerrain(null);
      this.map.easeTo({ pitch: 0, duration: 600 });
    }
  }

  setExageracion(v: number): void {
    if (this.map.getTerrain()) this.map.setTerrain({ source: 'src-terreno', exaggeration: v });
  }

  // ---------- Datos vectoriales ----------

  setZona(datos: FeatureCollectionSimple): void {
    (this.map.getSource('zona') as GeoJSONSource).setData(datos as unknown as GeoJSON);
    // Los estados no persisten al cambiar los datos si cambian los ids: se reaplican.
    requestAnimationFrame(() => {
      for (const rc of this.resultadosRcs) this.map.setFeatureState({ source: 'zona', id: rc }, { resultado: true });
      if (this.seleccionRc) this.map.setFeatureState({ source: 'zona', id: this.seleccionRc }, { seleccionada: true });
    });
  }

  setPartes(datos: FeatureCollectionSimple): void {
    (this.map.getSource('partes') as GeoJSONSource).setData(datos as unknown as GeoJSON);
  }

  setAgua(datos: FeatureCollectionLineas): void {
    (this.map.getSource('agua') as GeoJSONSource).setData(datos as unknown as GeoJSON);
  }

  setResultados(rcs: string[]): void {
    for (const rc of this.resultadosRcs) this.map.setFeatureState({ source: 'zona', id: rc }, { resultado: false });
    this.resultadosRcs = new Set(rcs);
    for (const rc of this.resultadosRcs) this.map.setFeatureState({ source: 'zona', id: rc }, { resultado: true });
  }

  setSeleccion(rc: string | null): void {
    if (this.seleccionRc) this.map.setFeatureState({ source: 'zona', id: this.seleccionRc }, { seleccionada: false });
    this.seleccionRc = rc;
    if (rc) this.map.setFeatureState({ source: 'zona', id: rc }, { seleccionada: true });
  }

  setHover(rc: string | null): void {
    if (this.hoverRc && this.hoverRc !== rc) this.map.setFeatureState({ source: 'zona', id: this.hoverRc }, { hover: false });
    this.hoverRc = rc;
    if (rc) this.map.setFeatureState({ source: 'zona', id: rc }, { hover: true });
  }

  setPuntos(puntos: [number, number][]): void {
    (this.map.getSource('puntos') as GeoJSONSource).setData({
      type: 'FeatureCollection',
      features: puntos.map((p) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: p }, properties: {} })),
    });
  }

  /** Dibuja la zona de carga: el limite municipal si lo hay, o el rectangulo (bbox en UTM). */
  mostrarZona(zona: { bbox: Bbox; epsg: number; poligono?: MultiPoligono } | null): void {
    const src = this.map.getSource('dibujo') as GeoJSONSource;
    if (!zona) { src.setData({ type: 'FeatureCollection', features: [] }); return; }
    if (zona.poligono && zona.poligono.length) {
      const coordinates = zona.poligono.map((pol) => pol.map((an) => { const r = proyectarAnillo(zona.epsg, 4326, an); return r.length ? [...r, r[0]] : r; }));
      src.setData({ type: 'FeatureCollection', features: [{ type: 'Feature', geometry: { type: 'MultiPolygon', coordinates }, properties: {} }] });
      return;
    }
    const [a, b, c, d] = zona.bbox;
    const anillo: Punto[] = [[a, b], [c, b], [c, d], [a, d], [a, b]].map((p) => proyectar(zona.epsg, 4326, p as Punto));
    src.setData({ type: 'FeatureCollection', features: [{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [anillo] }, properties: {} }] });
  }

  // ---------- Herramientas ----------

  setHerramienta(h: 'ninguna' | 'zona' | 'medir' | 'punto'): void {
    this.herramienta = h;
    this.map.getCanvas().style.cursor = h === 'ninguna' ? '' : 'crosshair';
    if (h === 'zona') this.map.dragPan.disable(); else this.map.dragPan.enable();
    if (h !== 'medir') { this.medicion = []; this.pintarMedicion(); this.onMedicion?.(null); }
  }

  private conectarEventos(): void {
    const m = this.map;
    m.on('mousemove', (e) => {
      this.onCursor?.([e.lngLat.lng, e.lngLat.lat]);
      if (this.herramienta === 'zona' && this.dibujando) {
        const fin = this.aUtm(e.lngLat.lng, e.lngLat.lat, this.dibujando.epsg);
        this.pintarRectangulo(this.dibujando.inicio, fin, this.dibujando.epsg);
        return;
      }
      if (this.herramienta !== 'ninguna') return;
      const f = m.queryRenderedFeatures(e.point, { layers: ['zona-fill'] })[0];
      const rc = f ? String(f.id) : null;
      this.setHover(rc);
      m.getCanvas().style.cursor = rc ? 'pointer' : '';
    });
    m.on('mouseout', () => { this.setHover(null); this.onCursor?.(null); });

    // Rectangulo de zona: con el raton y, en el movil y la tableta, con el dedo.
    const empezarZona = (e: { lngLat: { lng: number; lat: number }; preventDefault: () => void }) => {
      if (this.herramienta !== 'zona') return;
      e.preventDefault();
      const epsg = this.epsgVista();
      this.dibujando = { inicio: this.aUtm(e.lngLat.lng, e.lngLat.lat, epsg), epsg };
    };
    const acabarZona = (e: { lngLat: { lng: number; lat: number } }) => {
      if (this.herramienta !== 'zona' || !this.dibujando) return;
      const { inicio, epsg } = this.dibujando;
      this.dibujando = null;
      const fin = this.aUtm(e.lngLat.lng, e.lngLat.lat, epsg);
      const bbox: Bbox = [Math.min(inicio[0], fin[0]), Math.min(inicio[1], fin[1]), Math.max(inicio[0], fin[0]), Math.max(inicio[1], fin[1])];
      if (bbox[2] - bbox[0] < 20 || bbox[3] - bbox[1] < 20) return;
      this.setHerramienta('ninguna');
      this.onZonaDibujada?.({ bbox, epsg });
    };
    m.on('mousedown', empezarZona);
    m.on('mouseup', acabarZona);
    m.on('touchstart', (e) => { if (e.points.length === 1) empezarZona(e); });
    m.on('touchmove', (e) => {
      if (this.herramienta !== 'zona' || !this.dibujando) return;
      e.preventDefault();
      this.pintarRectangulo(this.dibujando.inicio, this.aUtm(e.lngLat.lng, e.lngLat.lat, this.dibujando.epsg), this.dibujando.epsg);
    });
    m.on('touchend', acabarZona);

    m.on('click', (e: MapMouseEvent) => {
      if (this.herramienta === 'medir') {
        if (this.medicion.length === 0) this.epsgMedicion = this.epsgVista();
        this.medicion.push(this.aUtm(e.lngLat.lng, e.lngLat.lat, this.epsgMedicion));
        this.pintarMedicion();
        return;
      }
      if (this.herramienta === 'punto') {
        this.onPuntoElegido?.([e.lngLat.lng, e.lngLat.lat]);
        this.setHerramienta('ninguna');
        return;
      }
      if (this.herramienta !== 'ninguna') return;
      const f = m.queryRenderedFeatures(e.point, { layers: ['zona-fill'] })[0];
      this.onClickParcela?.({ rc: f ? String(f.id) : null, lonLat: [e.lngLat.lng, e.lngLat.lat] });
    });
    m.on('dblclick', (e) => {
      if (this.herramienta === 'medir') { e.preventDefault(); this.setHerramienta('ninguna'); }
    });
    m.on('moveend', () => this.onMovimiento?.(this.vista()));
  }

  private aUtm(lon: number, lat: number, epsg: number): Punto {
    const r = aUtm(lon, lat, epsg);
    return [r.x, r.y];
  }

  private pintarRectangulo(a: Punto, b: Punto, epsg: number): void {
    const bbox: Bbox = [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[0], b[0]), Math.max(a[1], b[1])];
    this.mostrarZona({ bbox, epsg });
  }

  private pintarMedicion(): void {
    const src = this.map.getSource('medicion') as GeoJSONSource;
    const pts = this.medicion.map((p) => proyectar(this.epsgMedicion, 4326, p));
    const features: Feature[] = pts.map((p) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: p }, properties: {} }));
    if (pts.length >= 2) features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: pts }, properties: {} });
    if (pts.length >= 3) features.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...pts, pts[0]]] }, properties: {} });
    src.setData({ type: 'FeatureCollection', features });
    if (this.medicion.length === 0) { this.onMedicion?.(null); return; }
    this.onMedicion?.({
      puntos: [...this.medicion],
      longitudM: longitud(this.medicion),
      areaM2: this.medicion.length >= 3 ? areaAnillo(this.medicion) : null,
    });
  }

  /** Captura del lienzo para los informes (requiere preserveDrawingBuffer o redibujar antes). */
  async capturar(): Promise<string> {
    return new Promise((resolver) => {
      this.map.once('render', () => resolver(this.map.getCanvas().toDataURL('image/png')));
      this.map.triggerRepaint();
    });
  }

  centroLngLat(): LngLatLike {
    return this.map.getCenter();
  }
}
